import {
  forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef, useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Hand, Minus, MousePointer2, Plus, Ruler, ZoomIn } from 'lucide-react';
import type { PrintCrop, PrintPanel, PrintPlacement, PrintTemplate, PrintView } from '@wrapforce/shared';
import { activeOutlineViews, buildVehicleOutline, outlineViewFor } from '@wrapforce/shared';
import {
  clampCrop, computeFitCamera, computeLayerResize, computeMoveSnap, computeRulerTicks, computeZoomAtPoint, isTailLightFeature,
  isTypingTarget, panelArea, panelLabelAnchor, panelPath, panelsForView, rotatePoint, screenToInches,
} from './helpers';
import type { RulerTick } from './helpers';
import type { Camera } from './helpers';
import type { EditorArtwork } from './PlacementEditor';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

type CornerSign = { sx: -1 | 1; sy: -1 | 1 };
const CORNERS: Record<'nw' | 'ne' | 'sw' | 'se', CornerSign> = {
  nw: { sx: -1, sy: -1 },
  ne: { sx: 1, sy: -1 },
  sw: { sx: -1, sy: 1 },
  se: { sx: 1, sy: 1 },
};

export type CanvasTool = 'move' | 'hand' | 'zoom';

export interface PrintCanvasHandle {
  zoomIn: () => void;
  zoomOut: () => void;
  zoomToFit: () => void;
  zoomTo100: () => void;
  setTool: (tool: CanvasTool) => void;
}

// Snap threshold in screen pixels (converted to inches through the camera's
// current scale) and the multiplicative bounds on zoom, relative to the
// document's fit-to-screen scale (which stands in for "100%" - see the
// zoomPct readout below).
const SNAP_THRESHOLD_PX = 6;
const MIN_ZOOM_MULT = 0.05;
const MAX_ZOOM_MULT = 32;
const WHEEL_ZOOM_SPEED = 0.0025;
const CLICK_ZOOM_FACTOR = 1.5;
const KEY_ZOOM_FACTOR = 1.25;

// Thickness (px) of each Photoshop-style ruler band along the viewport's top
// and left edges, and how many minor ticks subdivide each major step.
const RULER_SIZE_PX = 20;
const RULER_MINOR_DIVISIONS = 4;

function cornerWorld(layer: PrintPlacement, corner: CornerSign): { x: number; y: number } {
  const cx = layer.x + layer.w / 2;
  const cy = layer.y + layer.h / 2;
  const local = rotatePoint((corner.sx * layer.w) / 2, (corner.sy * layer.h) / 2, layer.rotationDeg);
  return { x: cx + local.x, y: cy + local.y };
}

type LabelBox = { x: number; y: number; w: number; h: number };

function boxesIntersect(a: LabelBox, b: LabelBox): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

// Per-panel label plan: anchor, shrunk-to-fit font size, and whether the
// collision pass keeps it visible. Font size starts at `baseFontSize` and
// shrinks (never below 60% of it) so the estimated text width - about
// 0.55 x fontSize x character count - fits 90% of the anchor's clearance;
// if it still doesn't fit at the floor, the label is hidden (the panel
// keeps its outline and <title> tooltip either way).
function planPanelLabel(panel: PrintPanel, baseFontSize: number) {
  const anchor = panelLabelAnchor(panel);
  const availableWidth = anchor.maxWidth * 0.9;
  const estimateWidth = (fontSize: number) => 0.55 * fontSize * panel.label.length;
  const minFontSize = baseFontSize * 0.6;
  let fontSize = baseFontSize;
  if (estimateWidth(fontSize) > availableWidth && panel.label.length > 0) {
    fontSize = Math.max(minFontSize, availableWidth / (0.55 * panel.label.length));
  }
  const show = estimateWidth(fontSize) <= availableWidth || panel.label.length === 0;
  const w = estimateWidth(fontSize);
  const h = fontSize * 1.2;
  const box: LabelBox = { x: anchor.x - w / 2, y: anchor.y - h / 2, w, h };
  return { panel, anchor, fontSize, show, box };
}

// Resolves label collisions largest-panel-first: a label that overlaps one
// already placed is hidden, except a 'mirror' label always loses (even if
// it was placed first) since it's the least useful one to keep legible.
function resolveLabelCollisions(plans: ReturnType<typeof planPanelLabel>[]): ReturnType<typeof planPanelLabel>[] {
  const ordered = [...plans].sort((a, b) => panelArea(b.panel) - panelArea(a.panel));
  const placed: Array<{ box: LabelBox; isMirror: boolean; plan: ReturnType<typeof planPanelLabel> }> = [];
  for (const plan of ordered) {
    if (!plan.show) continue;
    const collidingIdx = placed.findIndex((p) => boxesIntersect(p.box, plan.box));
    if (collidingIdx === -1) {
      placed.push({ box: plan.box, isMirror: plan.panel.key === 'mirror', plan });
      continue;
    }
    const colliding = placed[collidingIdx]!;
    if (colliding.isMirror && plan.panel.key !== 'mirror') {
      colliding.plan.show = false;
      placed.splice(collidingIdx, 1, { box: plan.box, isMirror: false, plan });
    } else {
      plan.show = false;
    }
  }
  return ordered;
}

type DragState =
  | { mode: 'move'; layerId: string; startPointer: { x: number; y: number }; start: PrintPlacement }
  | { mode: 'resize'; layerId: string; corner: keyof typeof CORNERS; start: PrintPlacement }
  | { mode: 'rotate'; layerId: string; center: { x: number; y: number } }
  | { mode: 'crop-pan'; layerId: string; startPointer: { x: number; y: number }; startCrop: PrintCrop; fullW: number; fullH: number }
  | { mode: 'crop-resize'; layerId: string; corner: keyof typeof CORNERS; anchorLocal: { x: number; y: number }; startCrop: PrintCrop; w: number; h: number }
  | null;

type PanState = { startClientX: number; startClientY: number; startPanX: number; startPanY: number } | null;

// Local (unrotated) full-image rect for the current crop fraction, in the
// layer's own coordinate frame centered on its footprint.
function fullImageLocalRect(layer: PrintPlacement, crop: PrintCrop): { x: number; y: number; w: number; h: number } {
  const fullW = layer.w / crop.w;
  const fullH = layer.h / crop.h;
  return { x: -layer.w / 2 - crop.x * fullW, y: -layer.h / 2 - crop.y * fullH, w: fullW, h: fullH };
}

function LayerImage({
  artwork, crop, w, h, opacity, cursor, onPointerDown,
}: {
  artwork: EditorArtwork | undefined;
  crop: PrintCrop;
  w: number;
  h: number;
  opacity: number;
  cursor?: string;
  onPointerDown?: (e: ReactPointerEvent) => void;
}) {
  if (!artwork?.objectUrl || !artwork.width || !artwork.height) return null;
  const natW = artwork.width;
  const natH = artwork.height;
  return (
    <svg
      x={-w / 2}
      y={-h / 2}
      width={w}
      height={h}
      viewBox={`${crop.x * natW} ${crop.y * natH} ${crop.w * natW} ${crop.h * natH}`}
      preserveAspectRatio="none"
      opacity={opacity}
      style={{ cursor, pointerEvents: onPointerDown ? 'auto' : 'none' }}
      onPointerDown={onPointerDown}
    >
      <image href={artwork.objectUrl} x={0} y={0} width={natW} height={natH} preserveAspectRatio="none" />
    </svg>
  );
}

// One Photoshop-style ruler band (top edge for axis 'x', left edge for axis
// 'y'), rendered from the same camera as the main viewport so it stays in
// sync with pan/zoom. Pure tick math lives in computeRulerTicks (helpers.ts);
// this only lays the ticks, the document-extent band, the selection
// highlight and the cursor marker out as SVG. Colored with `currentColor` so
// it inherits the wrapping element's (theme-aware) text color.
function RulerBar({
  axis, viewportPx, scale, pan, docLengthIn, cursorValueIn, selectionRange,
}: {
  axis: 'x' | 'y';
  viewportPx: number;
  scale: number;
  pan: number;
  docLengthIn: number;
  cursorValueIn: number | null;
  selectionRange: { min: number; max: number } | null;
}) {
  const ticks = useMemo<RulerTick[]>(
    () => computeRulerTicks({ scale, pan, viewportPx, minorDivisions: RULER_MINOR_DIVISIONS }),
    [scale, pan, viewportPx],
  );
  const isHorizontal = axis === 'x';
  const docStartPx = pan;
  const docEndPx = pan + docLengthIn * scale;

  function bandRect(startPx: number, endPx: number) {
    return isHorizontal
      ? { x: startPx, y: 0, w: endPx - startPx, h: RULER_SIZE_PX }
      : { x: 0, y: startPx, w: RULER_SIZE_PX, h: endPx - startPx };
  }

  const docBand = bandRect(docStartPx, docEndPx);
  const selectionBand = selectionRange ? bandRect(pan + selectionRange.min * scale, pan + selectionRange.max * scale) : null;

  return (
    <svg width={isHorizontal ? viewportPx : RULER_SIZE_PX} height={isHorizontal ? RULER_SIZE_PX : viewportPx} style={{ display: 'block' }}>
      <rect x={docBand.x} y={docBand.y} width={docBand.w} height={docBand.h} fill="currentColor" fillOpacity={0.08} />
      {selectionBand ? <rect x={selectionBand.x} y={selectionBand.y} width={selectionBand.w} height={selectionBand.h} fill="var(--accent, #2563eb)" fillOpacity={0.3} /> : null}
      {ticks.map((tick) => {
        const tickLen = tick.major ? RULER_SIZE_PX * 0.5 : RULER_SIZE_PX * 0.25;
        return (
          <g key={tick.valueIn}>
            <line
              x1={isHorizontal ? tick.pos : RULER_SIZE_PX - tickLen}
              y1={isHorizontal ? RULER_SIZE_PX - tickLen : tick.pos}
              x2={isHorizontal ? tick.pos : RULER_SIZE_PX}
              y2={isHorizontal ? RULER_SIZE_PX : tick.pos}
              stroke="currentColor"
              strokeOpacity={tick.major ? 0.75 : 0.4}
              strokeWidth={1}
            />
            {tick.major ? (
              isHorizontal ? (
                <text x={tick.pos + 2} y={9} fontSize={9} fill="currentColor" fillOpacity={0.85} textAnchor="start">{Math.round(tick.valueIn)}</text>
              ) : (
                <text x={2} y={tick.pos + 3} fontSize={8} fill="currentColor" fillOpacity={0.85} textAnchor="start">{Math.round(tick.valueIn)}</text>
              )
            ) : null}
          </g>
        );
      })}
      {cursorValueIn !== null ? (
        <line
          x1={isHorizontal ? pan + cursorValueIn * scale : 0}
          y1={isHorizontal ? 0 : pan + cursorValueIn * scale}
          x2={isHorizontal ? pan + cursorValueIn * scale : RULER_SIZE_PX}
          y2={isHorizontal ? RULER_SIZE_PX : pan + cursorValueIn * scale}
          stroke="#ec4899"
          strokeWidth={1}
        />
      ) : null}
    </svg>
  );
}

export const PrintCanvas = forwardRef<PrintCanvasHandle, {
  template: PrintTemplate;
  view: PrintView;
  layers: PrintPlacement[];
  artworks: Record<string, EditorArtwork>;
  selectedLayerId: string | null;
  cropMode: boolean;
  cropDraft: PrintCrop | null;
  fillContainer?: boolean;
  // Shows exactly what will be printed/installed: no ghosts, no panel
  // outlines/labels/grid, no selection UI, and glass is drawn opaque on top
  // of the artwork so it visibly stops at every window. Read-only.
  previewMode?: boolean;
  // Draws the glass polygons on top of the artwork (both in edit and
  // preview mode) so the designer/customer can see exactly where each
  // window cuts through the design. OFF hides glass entirely in both
  // modes. Wheels are unaffected either way. Has no visible effect on a
  // view whose AI-drawn outline is in use (see outlineUrls) - glass is
  // already baked into that drawing and excluded from the mask.
  showWindows?: boolean;
  // AI-drawn outline layer images for this design, keyed by media id (see
  // useOutlineLayerUrls). When this view's outline is ready AND its three
  // layers are all present here, the outline's own image/mask/lines drawing
  // replaces the parametric vehicle body/glass/wheels/lights/trim/tyres for
  // this view - see outlineViewFor in @wrapforce/shared.
  outlineUrls?: Record<string, string>;
  onSelectLayer: (id: string | null) => void;
  onGestureStart: () => void;
  onGestureChange: (id: string, next: PrintPlacement) => void;
  onGestureEnd: (id: string, next: PrintPlacement) => void;
  onCropDraftChange: (next: PrintCrop) => void;
}>(function PrintCanvas({
  template, view, layers, artworks, selectedLayerId, cropMode, cropDraft,
  fillContainer = false, previewMode = false, showWindows = true, outlineUrls = {},
  onSelectLayer, onGestureStart, onGestureChange, onGestureEnd, onCropDraftChange,
}, ref) {
  const { t } = useTranslation('printWraps');
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState>(null);
  const panRef = useRef<PanState>(null);
  const clipId = useId();
  const outlineMaskId = useId();
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);
  const [camera, setCamera] = useState<Camera>({ scale: 1, panX: 0, panY: 0 });
  const [activeTool, setActiveTool] = useState<CanvasTool>('move');
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [cursorIn, setCursorIn] = useState<{ x: number; y: number } | null>(null);
  const [guides, setGuides] = useState<{ x: number | null; y: number | null }>({ x: null, y: null });
  const [showRulers, setShowRulers] = useState(true);
  const spaceHeldRef = useRef(false);
  const lastFitViewRef = useRef<PrintView | null>(null);
  // True until the user zooms or pans by hand - while true the camera keeps
  // re-fitting as the viewport settles to its real size (its first
  // measurement often happens mid layout).
  const autoFitRef = useRef(true);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerSize({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const viewDims = template.views[view];
  const panels = useMemo(() => panelsForView(template, view), [template, view]);
  const widthIn = viewDims?.widthIn ?? template.overall.lengthIn;
  const heightIn = viewDims?.heightIn ?? template.overall.heightIn;
  const outline = useMemo(() => buildVehicleOutline(template, view), [template, view]);
  const outlineViewInfo = useMemo(() => outlineViewFor(view), [view]);
  const activeViews = useMemo(() => activeOutlineViews(template), [template]);
  const outlineViewData = outlineViewInfo && activeViews ? activeViews[outlineViewInfo.source] : undefined;
  const outlineMirror = outlineViewInfo?.mirror ?? false;
  const outlineImageUrl = outlineViewData ? outlineUrls[outlineViewData.imageMediaId] : undefined;
  const outlineMaskUrl = outlineViewData ? outlineUrls[outlineViewData.maskMediaId] : undefined;
  const outlineLinesUrl = outlineViewData ? outlineUrls[outlineViewData.linesMediaId] : undefined;
  // Only switch to the outline drawing once all three of its layers have
  // actually loaded - until then this view keeps rendering the parametric
  // template, exactly as before the outline existed.
  const useOutlineDrawing = !!(outlineImageUrl && outlineMaskUrl && outlineLinesUrl);
  const outlineTransform = outlineMirror ? `translate(${widthIn} 0) scale(-1 1)` : undefined;
  const labelPlans = useMemo(() => {
    const baseFontSize = heightIn / 40;
    return resolveLabelCollisions(panels.map((panel) => planPanelLabel(panel, baseFontSize)));
  }, [panels, heightIn]);

  const selectedLayer = previewMode ? null : layers.find((l) => l.id === selectedLayerId) ?? null;

  // Fit-to-screen is treated as "100%" for the zoom readout and for the
  // min/max zoom clamp - re-fit whenever the active view tab changes, and on
  // container resizes until the user zooms or pans by hand, so the user's own
  // zoom/pan survives incidental resizes (e.g. toggling fullscreen).
  const fitCamera = containerSize ? computeFitCamera(containerSize.w, containerSize.h, widthIn, heightIn) : null;
  const fitScale = fitCamera?.scale ?? camera.scale;

  useEffect(() => {
    if (!containerSize) return;
    if (lastFitViewRef.current === view && !autoFitRef.current) return;
    if (lastFitViewRef.current !== view) autoFitRef.current = true;
    lastFitViewRef.current = view;
    setCamera(computeFitCamera(containerSize.w, containerSize.h, widthIn, heightIn));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, containerSize, widthIn, heightIn]);

  function applyZoom(factor: number, screenX: number, screenY: number) {
    autoFitRef.current = false;
    setCamera((c) => {
      const min = fitScale * MIN_ZOOM_MULT;
      const max = fitScale * MAX_ZOOM_MULT;
      const nextScale = Math.min(max, Math.max(min, c.scale * factor));
      return computeZoomAtPoint(c, screenX, screenY, nextScale);
    });
  }

  function applyZoomAtCenter(factor: number) {
    const cx = (containerSize?.w ?? 0) / 2;
    const cy = (containerSize?.h ?? 0) / 2;
    applyZoom(factor, cx, cy);
  }

  const applyZoomRef = useRef(applyZoom);
  applyZoomRef.current = applyZoom;

  // Non-passive native wheel listener: React's synthetic onWheel handler is
  // registered passively, so preventDefault() there would not stop the page
  // from scrolling while zooming with the mouse wheel or a trackpad pinch.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const rect = el!.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const dy = Math.max(-200, Math.min(200, e.deltaY));
      const factor = Math.exp(-dy * WHEEL_ZOOM_SPEED);
      applyZoomRef.current(factor, sx, sy);
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Hold Space to temporarily pan regardless of the active tool, exactly
  // like Photoshop's Hand tool shortcut.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.code !== 'Space' || isTypingTarget(e.target) || e.repeat) return;
      e.preventDefault();
      spaceHeldRef.current = true;
      setSpaceHeld(true);
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.code !== 'Space') return;
      spaceHeldRef.current = false;
      setSpaceHeld(false);
    }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  // Cmd/Ctrl+R toggles the rulers, matching Photoshop's shortcut - must
  // preventDefault or the browser reloads the page instead.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'r' && e.key !== 'R') return;
      if (!(e.metaKey || e.ctrlKey) || isTypingTarget(e.target)) return;
      e.preventDefault();
      setShowRulers((prev) => !prev);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useImperativeHandle(ref, () => ({
    zoomIn: () => applyZoomAtCenter(KEY_ZOOM_FACTOR),
    zoomOut: () => applyZoomAtCenter(1 / KEY_ZOOM_FACTOR),
    zoomToFit: () => {
      if (!containerSize) return;
      autoFitRef.current = true;
      setCamera(computeFitCamera(containerSize.w, containerSize.h, widthIn, heightIn));
    },
    zoomTo100: () => {
      const cx = (containerSize?.w ?? 0) / 2;
      const cy = (containerSize?.h ?? 0) / 2;
      autoFitRef.current = false;
      setCamera((c) => computeZoomAtPoint(c, cx, cy, fitScale));
    },
    setTool: (tool) => setActiveTool(tool),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [containerSize, widthIn, heightIn, fitScale]);

  const canInteractLayers = activeTool === 'move' && !spaceHeldRef.current;

  function toUserPoint(clientX: number, clientY: number): { x: number; y: number } {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return screenToInches(camera, clientX - rect.left, clientY - rect.top);
  }

  function beginLayerDrag(e: ReactPointerEvent, layer: PrintPlacement, mode: 'move' | 'rotate' | keyof typeof CORNERS) {
    // Leave the middle button (and anything but the primary button) alone so
    // it bubbles up to the canvas's own pan handling, even over a layer.
    if (!canInteractLayers || e.button !== 0) return;
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    onSelectLayer(layer.id);
    const p = toUserPoint(e.clientX, e.clientY);
    if (mode === 'move') {
      onGestureStart();
      dragRef.current = { mode: 'move', layerId: layer.id, startPointer: p, start: layer };
    } else if (mode === 'rotate') {
      onGestureStart();
      dragRef.current = { mode: 'rotate', layerId: layer.id, center: { x: layer.x + layer.w / 2, y: layer.y + layer.h / 2 } };
    } else {
      onGestureStart();
      dragRef.current = { mode: 'resize', layerId: layer.id, corner: mode, start: layer };
    }
  }

  function beginCropDrag(e: ReactPointerEvent, kind: 'pan' | keyof typeof CORNERS) {
    if (!canInteractLayers || e.button !== 0) return;
    if (!selectedLayer || !cropDraft) return;
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    const p = toUserPoint(e.clientX, e.clientY);
    const local = rotatePoint(p.x - (selectedLayer.x + selectedLayer.w / 2), p.y - (selectedLayer.y + selectedLayer.h / 2), -selectedLayer.rotationDeg);
    if (kind === 'pan') {
      const full = fullImageLocalRect(selectedLayer, cropDraft);
      dragRef.current = { mode: 'crop-pan', layerId: selectedLayer.id, startPointer: local, startCrop: cropDraft, fullW: full.w, fullH: full.h };
    } else {
      const full = fullImageLocalRect(selectedLayer, cropDraft);
      const sign = CORNERS[kind];
      const anchorLocal = {
        x: sign.sx === 1 ? full.x : full.x + full.w,
        y: sign.sy === 1 ? full.y : full.y + full.h,
      };
      dragRef.current = { mode: 'crop-resize', layerId: selectedLayer.id, corner: kind, anchorLocal, startCrop: cropDraft, w: selectedLayer.w, h: selectedLayer.h };
    }
  }

  function handleCanvasPointerDown(e: ReactPointerEvent<SVGSVGElement>) {
    if (dragRef.current) return;
    const isPanTrigger = e.button === 1 || activeTool === 'hand' || spaceHeldRef.current;
    if (isPanTrigger) {
      e.preventDefault();
      containerRef.current?.setPointerCapture(e.pointerId);
      panRef.current = { startClientX: e.clientX, startClientY: e.clientY, startPanX: camera.panX, startPanY: camera.panY };
      setIsPanning(true);
      return;
    }
    if (activeTool === 'zoom' && e.button === 0) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      applyZoom(e.altKey ? 1 / CLICK_ZOOM_FACTOR : CLICK_ZOOM_FACTOR, e.clientX - rect.left, e.clientY - rect.top);
    }
  }

  function handlePointerMove(e: ReactPointerEvent) {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) setCursorIn(screenToInches(camera, e.clientX - rect.left, e.clientY - rect.top));

    if (panRef.current) {
      const dx = e.clientX - panRef.current.startClientX;
      const dy = e.clientY - panRef.current.startClientY;
      autoFitRef.current = false;
      setCamera((c) => ({ ...c, panX: panRef.current!.startPanX + dx, panY: panRef.current!.startPanY + dy }));
      return;
    }

    const drag = dragRef.current;
    if (!drag) return;
    const p = toUserPoint(e.clientX, e.clientY);

    if (drag.mode === 'move') {
      const dx = p.x - drag.startPointer.x;
      const dy = p.y - drag.startPointer.y;
      let nextX = drag.start.x + dx;
      let nextY = drag.start.y + dy;
      if (!e.ctrlKey && !e.metaKey) {
        const others = layers.filter((l) => l.id !== drag.layerId);
        const snapped = computeMoveSnap({
          x: nextX, y: nextY, w: drag.start.w, h: drag.start.h, others,
          viewWidthIn: widthIn, viewHeightIn: heightIn, thresholdIn: SNAP_THRESHOLD_PX / camera.scale,
        });
        nextX = snapped.x;
        nextY = snapped.y;
        setGuides({ x: snapped.guideX, y: snapped.guideY });
      } else if (guides.x !== null || guides.y !== null) {
        setGuides({ x: null, y: null });
      }
      onGestureChange(drag.layerId, { ...drag.start, x: nextX, y: nextY });
      return;
    }

    if (drag.mode === 'rotate') {
      const dx = p.x - drag.center.x;
      const dy = p.y - drag.center.y;
      let rotationDeg = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
      if (e.shiftKey) rotationDeg = Math.round(rotationDeg / 15) * 15;
      rotationDeg = Math.max(-180, Math.min(180, rotationDeg));
      const layer = layers.find((l) => l.id === drag.layerId);
      if (layer) onGestureChange(drag.layerId, { ...layer, rotationDeg });
      return;
    }

    if (drag.mode === 'resize') {
      // Modifiers are read fresh from this move event, not fixed at
      // pointerdown, so pressing/releasing Shift or Alt mid-drag takes
      // effect immediately: free resize by default, Shift locks the
      // aspect ratio, Alt/Option resizes from the layer's center.
      const next = computeLayerResize({
        start: drag.start,
        corner: CORNERS[drag.corner],
        pointerWorld: p,
        aspectLocked: e.shiftKey,
        fromCenter: e.altKey,
      });
      onGestureChange(drag.layerId, next);
      return;
    }

    if (drag.mode === 'crop-pan') {
      const layer = layers.find((l) => l.id === drag.layerId);
      if (!layer) return;
      const local = rotatePoint(p.x - (layer.x + layer.w / 2), p.y - (layer.y + layer.h / 2), -layer.rotationDeg);
      const dx = local.x - drag.startPointer.x;
      const dy = local.y - drag.startPointer.y;
      onCropDraftChange(clampCrop({
        ...drag.startCrop,
        x: drag.startCrop.x - dx / drag.fullW,
        y: drag.startCrop.y - dy / drag.fullH,
      }));
      return;
    }

    if (drag.mode === 'crop-resize') {
      const layer = layers.find((l) => l.id === drag.layerId);
      if (!layer) return;
      const local = rotatePoint(p.x - (layer.x + layer.w / 2), p.y - (layer.y + layer.h / 2), -layer.rotationDeg);
      const { anchorLocal, w, h } = drag;
      const newFullW = Math.max(w * 1.05, Math.abs(local.x - anchorLocal.x));
      const newFullH = Math.max(h * 1.05, Math.abs(local.y - anchorLocal.y));
      const fullX = Math.min(anchorLocal.x, local.x);
      const fullY = Math.min(anchorLocal.y, local.y);
      onCropDraftChange(clampCrop({
        x: -(w / 2 + fullX) / newFullW,
        y: -(h / 2 + fullY) / newFullH,
        w: w / newFullW,
        h: h / newFullH,
      }));
    }
  }

  function handlePointerUp() {
    if (panRef.current) {
      panRef.current = null;
      setIsPanning(false);
      return;
    }
    const drag = dragRef.current;
    dragRef.current = null;
    if (guides.x !== null || guides.y !== null) setGuides({ x: null, y: null });
    if (!drag) return;
    if (drag.mode === 'move' || drag.mode === 'rotate' || drag.mode === 'resize') {
      const layer = layers.find((l) => l.id === drag.layerId);
      if (layer) onGestureEnd(drag.layerId, layer);
    }
  }

  const gridStepIn = 12;
  const gridLinesX: number[] = [];
  for (let x = 0; x <= widthIn; x += gridStepIn) gridLinesX.push(x);
  const gridLinesY: number[] = [];
  for (let y = 0; y <= heightIn; y += gridStepIn) gridLinesY.push(y);

  const handleRadiusIn = (Math.max(widthIn, heightIn) / 60) * Math.min(2, fitScale / camera.scale || 1);

  const selectionCorners = selectedLayer && !cropMode
    ? { nw: cornerWorld(selectedLayer, CORNERS.nw), ne: cornerWorld(selectedLayer, CORNERS.ne), sw: cornerWorld(selectedLayer, CORNERS.sw), se: cornerWorld(selectedLayer, CORNERS.se) }
    : null;
  const rotateHandlePos = selectedLayer && !cropMode
    ? (() => {
        const cx = selectedLayer.x + selectedLayer.w / 2;
        const cy = selectedLayer.y + selectedLayer.h / 2;
        const topMid = rotatePoint(0, -selectedLayer.h / 2, selectedLayer.rotationDeg);
        const handleOffset = rotatePoint(0, -selectedLayer.h / 2 - Math.max(handleRadiusIn * 4, heightIn / 15), selectedLayer.rotationDeg);
        return { x: cx + handleOffset.x, y: cy + handleOffset.y, lineX: cx + topMid.x, lineY: cy + topMid.y };
      })()
    : null;

  const cropFullRect = selectedLayer && cropMode && cropDraft ? fullImageLocalRect(selectedLayer, cropDraft) : null;

  // The selected layer's axis-aligned extent (world inches), for the ruler
  // overlay's selection highlight - independent of cropMode, unlike
  // selectionCorners above (which hides while cropping).
  const selectedLayerExtent = selectedLayer
    ? (() => {
        const corners = [CORNERS.nw, CORNERS.ne, CORNERS.sw, CORNERS.se].map((c) => cornerWorld(selectedLayer, c));
        return {
          x: { min: Math.min(...corners.map((c) => c.x)), max: Math.max(...corners.map((c) => c.x)) },
          y: { min: Math.min(...corners.map((c) => c.y)), max: Math.max(...corners.map((c) => c.y)) },
        };
      })()
    : null;

  const zoomPct = Math.round((camera.scale / (fitScale || 1)) * 100);
  const guideStrokeWidth = 1 / camera.scale;

  const cursorClass = cropMode
    ? undefined
    : isPanning
      ? 'cursor-grabbing'
      : activeTool === 'hand' || spaceHeld
        ? 'cursor-grab'
        : activeTool === 'zoom'
          ? 'cursor-zoom-in'
          : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', fillContainer && 'h-full min-h-0')}>
      <div className="flex flex-wrap items-center gap-1.5 border-b border-border pb-1.5">
        <Button type="button" size="sm" variant={activeTool === 'move' ? 'primary' : 'outline'} onClick={() => setActiveTool('move')} aria-label={t('editor.toolbar.moveTool')} title={t('editor.toolbar.moveTool')}>
          <MousePointer2 size={14} />
        </Button>
        <Button type="button" size="sm" variant={activeTool === 'hand' ? 'primary' : 'outline'} onClick={() => setActiveTool('hand')} aria-label={t('editor.toolbar.handTool')} title={t('editor.toolbar.handTool')}>
          <Hand size={14} />
        </Button>
        <Button type="button" size="sm" variant={activeTool === 'zoom' ? 'primary' : 'outline'} onClick={() => setActiveTool('zoom')} aria-label={t('editor.toolbar.zoomTool')} title={t('editor.toolbar.zoomTool')}>
          <ZoomIn size={14} />
        </Button>
        <div className="mx-1 h-5 w-px bg-border" />
        <Button type="button" size="sm" variant="outline" onClick={() => applyZoomAtCenter(1 / KEY_ZOOM_FACTOR)} aria-label={t('editor.toolbar.zoomOut')} title={t('editor.toolbar.zoomOut')}>
          <Minus size={14} />
        </Button>
        <span className="w-12 text-center text-xs font-mono text-muted">{t('editor.toolbar.zoomPct', { pct: zoomPct })}</span>
        <Button type="button" size="sm" variant="outline" onClick={() => applyZoomAtCenter(KEY_ZOOM_FACTOR)} aria-label={t('editor.toolbar.zoomIn')} title={t('editor.toolbar.zoomIn')}>
          <Plus size={14} />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            if (!containerSize) return;
            autoFitRef.current = true;
            setCamera(computeFitCamera(containerSize.w, containerSize.h, widthIn, heightIn));
          }}
          title={t('editor.toolbar.zoomFit')}
        >
          {t('editor.toolbar.zoomFit')}
        </Button>
        {[50, 100, 200, 400].map((pct) => (
          <Button
            key={pct}
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              const cx = (containerSize?.w ?? 0) / 2;
              const cy = (containerSize?.h ?? 0) / 2;
              autoFitRef.current = false;
              setCamera((c) => computeZoomAtPoint(c, cx, cy, fitScale * (pct / 100)));
            }}
            title={t('editor.toolbar.zoomPct', { pct })}
          >
            {t('editor.toolbar.zoomPct', { pct })}
          </Button>
        ))}
        <div className="mx-1 h-5 w-px bg-border" />
        <Button
          type="button"
          size="sm"
          variant={showRulers ? 'primary' : 'outline'}
          onClick={() => setShowRulers((prev) => !prev)}
          aria-pressed={showRulers}
          aria-label={showRulers ? t('editor.toolbar.hideRulers') : t('editor.toolbar.showRulers')}
          title={showRulers ? t('editor.toolbar.hideRulers') : t('editor.toolbar.showRulers')}
        >
          <Ruler size={14} />
        </Button>
      </div>

      <div
        ref={containerRef}
        className={cn('relative overflow-hidden border border-border bg-sidebar', fillContainer && 'flex-1', cursorClass)}
        style={fillContainer ? { height: '100%', width: '100%' } : { height: 'min(78vh, 640px)' }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        {containerSize ? (
          <svg
            ref={svgRef}
            width={containerSize.w}
            height={containerSize.h}
            style={{ display: 'block', touchAction: 'none' }}
            onPointerDown={handleCanvasPointerDown}
          >
            <g transform={`translate(${camera.panX} ${camera.panY}) scale(${camera.scale})`}>
              <rect x={0} y={0} width={widthIn} height={heightIn} fill="#ffffff" onPointerDown={previewMode || !canInteractLayers ? undefined : () => onSelectLayer(null)} />

              {/* Vehicle outline - a visual reference only, never printed. Wheels
                  draw here (under the layers) in edit mode, and again after the
                  layers in preview mode so they sit on top. Glass draws once,
                  always AFTER the layers (see below `showWindows`), whether
                  editing or previewing, so it never gets hidden under artwork.
                  When this view's AI-drawn outline is ready, its own flat drawing
                  (body, glass, wheels, lights, trim and tyres already baked in)
                  replaces all of the parametric shapes below. */}
              {useOutlineDrawing ? (
                <image href={outlineImageUrl} x={0} y={0} width={widthIn} height={heightIn} transform={outlineTransform} />
              ) : (
                <>
                  <path d={outline.body} fill="#F3F4F6" stroke="#9CA3AF" strokeWidth={widthIn / 300} />
                  {!previewMode ? (
                    outline.wheels.map((wheel, i) => (
                      <circle key={`wheel-${i}`} cx={wheel.cx} cy={wheel.cy} r={wheel.r} fill="#374151" />
                    ))
                  ) : null}
                </>
              )}

              {!previewMode ? (
                <>
                  {gridLinesX.map((x) => (
                    <line key={`gx-${x}`} x1={x} y1={0} x2={x} y2={heightIn} stroke="#16181D" strokeOpacity={0.1} strokeWidth={widthIn / 400} />
                  ))}
                  {gridLinesY.map((y) => (
                    <line key={`gy-${y}`} x1={0} y1={y} x2={widthIn} y2={y} stroke="#16181D" strokeOpacity={0.1} strokeWidth={widthIn / 400} />
                  ))}
                </>
              ) : null}

              <defs>
                <clipPath id={clipId}>
                  {panels.map((panel) => (
                    <path key={`${panel.view}:${panel.key}`} d={panelPath(panel)} />
                  ))}
                </clipPath>
                {useOutlineDrawing ? (
                  <mask id={outlineMaskId} maskUnits="userSpaceOnUse" x={0} y={0} width={widthIn} height={heightIn}>
                    <image href={outlineMaskUrl} x={0} y={0} width={widthIn} height={heightIn} transform={outlineTransform} />
                  </mask>
                ) : null}
              </defs>

              {layers.map((layer) => {
                const artwork = layer.artworkMediaId ? artworks[layer.artworkMediaId] : undefined;
                const cx = layer.x + layer.w / 2;
                const cy = layer.y + layer.h / 2;
                const isCropTarget = !previewMode && cropMode && layer.id === selectedLayerId && cropDraft;
                if (isCropTarget && cropFullRect) {
                  return (
                    <g key={layer.id} transform={`translate(${cx} ${cy}) rotate(${layer.rotationDeg})`}>
                      <rect
                        x={cropFullRect.x}
                        y={cropFullRect.y}
                        width={cropFullRect.w}
                        height={cropFullRect.h}
                        fill="transparent"
                        onPointerDown={(e) => beginCropDrag(e, 'pan')}
                        style={{ cursor: 'move' }}
                      />
                      <LayerImage artwork={artwork} crop={{ x: 0, y: 0, w: 1, h: 1 }} w={cropFullRect.w} h={cropFullRect.h} opacity={0.35} cursor="move" onPointerDown={(e) => beginCropDrag(e, 'pan')} />
                      <rect
                        x={-layer.w / 2}
                        y={-layer.h / 2}
                        width={layer.w}
                        height={layer.h}
                        fill="none"
                        stroke="var(--accent, #2563eb)"
                        strokeWidth={widthIn / 250}
                        strokeDasharray={`${widthIn / 90} ${widthIn / 140}`}
                      />
                      {(Object.keys(CORNERS) as Array<keyof typeof CORNERS>).map((key) => {
                        const sign = CORNERS[key];
                        const hx = sign.sx === 1 ? cropFullRect.x + cropFullRect.w : cropFullRect.x;
                        const hy = sign.sy === 1 ? cropFullRect.y + cropFullRect.h : cropFullRect.y;
                        return (
                          <circle
                            key={key}
                            cx={hx}
                            cy={hy}
                            r={handleRadiusIn}
                            fill="var(--accent, #2563eb)"
                            stroke="white"
                            strokeWidth={handleRadiusIn / 4}
                            onPointerDown={(e) => beginCropDrag(e, key)}
                            style={{ cursor: 'nwse-resize' }}
                          />
                        );
                      })}
                    </g>
                  );
                }

                return (
                  <g key={layer.id}>
                    {!previewMode ? (
                      <g transform={`translate(${cx} ${cy}) rotate(${layer.rotationDeg})`}>
                        <g transform={`scale(${layer.flipX ? -1 : 1} ${layer.flipY ? -1 : 1})`}>
                          <LayerImage
                            artwork={artwork}
                            crop={layer.crop}
                            w={layer.w}
                            h={layer.h}
                            opacity={0.15}
                            cursor={canInteractLayers ? 'move' : undefined}
                            onPointerDown={(e) => beginLayerDrag(e, layer, 'move')}
                          />
                        </g>
                      </g>
                    ) : null}
                    {/* Bug fix: the panel clip is applied on this OUTER, untransformed
                        <g>, wrapping the translated+rotated group - clipping an
                        <image> inside the transformed group shifts the clip rects
                        by (cx, cy) because userSpaceOnUse resolves against the
                        transform in effect on the element it is applied to. */}
                    <g clipPath={useOutlineDrawing ? undefined : `url(#${clipId})`} mask={useOutlineDrawing ? `url(#${outlineMaskId})` : undefined}>
                      <g transform={`translate(${cx} ${cy}) rotate(${layer.rotationDeg})`}>
                        <g transform={`scale(${layer.flipX ? -1 : 1} ${layer.flipY ? -1 : 1})`}>
                          <LayerImage
                            artwork={artwork}
                            crop={layer.crop}
                            w={layer.w}
                            h={layer.h}
                            opacity={1}
                            cursor={previewMode || !canInteractLayers ? undefined : 'move'}
                            onPointerDown={previewMode ? undefined : (e) => beginLayerDrag(e, layer, 'move')}
                          />
                        </g>
                      </g>
                    </g>
                  </g>
                );
              })}

              {useOutlineDrawing ? (
                // Seam/contour line art, drawn on top of the (masked) artwork so
                // panel gaps and body lines stay visible exactly like the AI
                // measurement's own reference drawing.
                <image href={outlineLinesUrl} x={0} y={0} width={widthIn} height={heightIn} transform={outlineTransform} style={{ pointerEvents: 'none' }} />
              ) : (
                <>
                  {showWindows ? (
                    outline.glass.map((d, i) => (
                      <path
                        key={`glass-${i}`}
                        d={d}
                        fill="#DBEAFE"
                        fillOpacity={previewMode ? 1 : 0.85}
                        stroke="#93C5FD"
                        strokeWidth={widthIn / 400}
                        style={{ pointerEvents: 'none' }}
                      />
                    ))
                  ) : null}
                  {previewMode ? (
                    outline.wheels.map((wheel, i) => (
                      <circle key={`preview-wheel-${i}`} cx={wheel.cx} cy={wheel.cy} r={wheel.r} fill="#374151" />
                    ))
                  ) : null}

                  {/* Lights, trim (grille/plate) and tyres are exclusion zones like
                      glass - panels never cover them and artwork never prints on
                      them - so they always draw on top of the artwork in both edit
                      and preview mode, regardless of the window toggle. A front view's
                      lights are all headlights, a rear view's are all tail lights;
                      a side view returns exactly [headlight, taillight] (see
                      buildSideLights in packages/shared). */}
                  {outline.lights.map((d, i) => {
                    const isTailLight = isTailLightFeature(view, i);
                    return (
                      <path
                        key={`light-${i}`}
                        d={d}
                        fill={isTailLight ? '#FEE2E2' : '#FEF3C7'}
                        stroke={isTailLight ? '#EF4444' : '#F59E0B'}
                        strokeWidth={widthIn / 400}
                        style={{ pointerEvents: 'none' }}
                      />
                    );
                  })}
                  {outline.trim.map((d, i) => (
                    <path key={`trim-${i}`} d={d} fill="#374151" fillOpacity={0.85} style={{ pointerEvents: 'none' }} />
                  ))}
                  {outline.tyres.map((tyre, i) => (
                    <rect key={`tyre-${i}`} x={tyre.x} y={tyre.y} width={tyre.w} height={tyre.h} rx={tyre.r} ry={tyre.r} fill="#1F2937" style={{ pointerEvents: 'none' }} />
                  ))}
                </>
              )}

              {!previewMode && !useOutlineDrawing ? panels.map((panel: PrintPanel) => {
                const plan = labelPlans.find((p) => p.panel === panel);
                const cutSize = `${panel.w.toFixed(1)} x ${panel.h.toFixed(1)} in`;
                return (
                  <g key={`${panel.view}:${panel.key}`}>
                    <path d={panelPath(panel)} fill="none" stroke="#16181D" strokeWidth={widthIn / 250} strokeOpacity={0.75}>
                      {/* Kept regardless of whether the visible label fits, so
                          hovering the panel still identifies it. */}
                      <title>{`${panel.label}, ${cutSize}`}</title>
                    </path>
                    {plan?.show ? (
                      <text
                        x={plan.anchor.x}
                        y={plan.anchor.y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={plan.fontSize}
                        fill="#16181D"
                        fillOpacity={0.85}
                        paintOrder="stroke"
                        stroke="#fff"
                        strokeWidth={plan.fontSize * 0.35}
                        strokeOpacity={0.9}
                      >
                        {panel.label}
                      </text>
                    ) : null}
                  </g>
                );
              }) : null}

              {!previewMode && selectionCorners ? (
                <>
                  {(Object.keys(CORNERS) as Array<keyof typeof CORNERS>).map((key) => (
                    <circle
                      key={key}
                      cx={selectionCorners[key].x}
                      cy={selectionCorners[key].y}
                      r={handleRadiusIn}
                      fill="var(--accent, #2563eb)"
                      stroke="white"
                      strokeWidth={handleRadiusIn / 4}
                      onPointerDown={(e) => beginLayerDrag(e, selectedLayer!, key)}
                      style={{ cursor: 'nwse-resize' }}
                    />
                  ))}
                  {rotateHandlePos ? (
                    <>
                      <line x1={rotateHandlePos.lineX} y1={rotateHandlePos.lineY} x2={rotateHandlePos.x} y2={rotateHandlePos.y} stroke="#16181D" strokeOpacity={0.5} strokeWidth={widthIn / 400} />
                      <circle
                        cx={rotateHandlePos.x}
                        cy={rotateHandlePos.y}
                        r={handleRadiusIn}
                        fill="#ffffff"
                        stroke="var(--accent, #2563eb)"
                        strokeWidth={handleRadiusIn / 3}
                        onPointerDown={(e) => beginLayerDrag(e, selectedLayer!, 'rotate')}
                        style={{ cursor: 'grab' }}
                      />
                    </>
                  ) : null}
                </>
              ) : null}

              {/* Smart guides: thin magenta lines while dragging a layer near the
                  document's edges/centerline or another layer's edges/center.
                  Hold Ctrl/Cmd while dragging to disable snapping temporarily. */}
              {guides.x !== null ? (
                <line x1={guides.x} y1={-heightIn} x2={guides.x} y2={heightIn * 2} stroke="#ec4899" strokeWidth={guideStrokeWidth} style={{ pointerEvents: 'none' }} />
              ) : null}
              {guides.y !== null ? (
                <line x1={-widthIn} y1={guides.y} x2={widthIn * 2} y2={guides.y} stroke="#ec4899" strokeWidth={guideStrokeWidth} style={{ pointerEvents: 'none' }} />
              ) : null}
            </g>
          </svg>
        ) : null}

        {/* Photoshop-style rulers, overlaid on top of the viewport (they
            never shift the canvas's own pointer math) - 0 at the document's
            top-left, adaptive tick spacing, a highlighted band for the
            document's extent and the selected layer's extent, and a marker
            line following the cursor. */}
        {containerSize && showRulers ? (
          <>
            <div
              className="pointer-events-none absolute left-0 top-0 z-10 overflow-hidden border-b border-border bg-surface-2 text-muted"
              style={{ width: containerSize.w, height: RULER_SIZE_PX }}
            >
              <RulerBar
                axis="x"
                viewportPx={containerSize.w}
                scale={camera.scale}
                pan={camera.panX}
                docLengthIn={widthIn}
                cursorValueIn={cursorIn?.x ?? null}
                selectionRange={selectedLayerExtent?.x ?? null}
              />
            </div>
            <div
              className="pointer-events-none absolute left-0 top-0 z-10 overflow-hidden border-r border-border bg-surface-2 text-muted"
              style={{ width: RULER_SIZE_PX, height: containerSize.h }}
            >
              <RulerBar
                axis="y"
                viewportPx={containerSize.h}
                scale={camera.scale}
                pan={camera.panY}
                docLengthIn={heightIn}
                cursorValueIn={cursorIn?.y ?? null}
                selectionRange={selectedLayerExtent?.y ?? null}
              />
            </div>
            <div
              className="pointer-events-none absolute left-0 top-0 z-20 border-b border-r border-border bg-surface-2"
              style={{ width: RULER_SIZE_PX, height: RULER_SIZE_PX }}
            />
          </>
        ) : null}
      </div>

      {previewMode ? null : (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted">
          <span>{t('editor.statusBar.zoom', { pct: zoomPct })}</span>
          {cursorIn ? (
            <span className="font-mono">{t('editor.statusBar.cursor', { x: cursorIn.x.toFixed(1), y: cursorIn.y.toFixed(1) })}</span>
          ) : null}
          {selectedLayer ? (
            <span className="font-mono">{t('editor.statusBar.selectedSize', { w: selectedLayer.w.toFixed(1), h: selectedLayer.h.toFixed(1) })}</span>
          ) : null}
        </div>
      )}
    </div>
  );
});
